/**
 * Business impact inputs (UI_SDD §R.9, SDD §35.7, DEC-117, DEC-118).
 *
 * These live on Settings rather than on the Trends page deliberately: they are workspace
 * configuration, and putting an editable parameter inside the chart that consumes it invites
 * tuning the input until the output looks right.
 *
 * Two rules the markup has to honour:
 *
 * 1. **Nothing is prefilled with a suggested value.** An empty field means the customer has
 *    not said, and that is exactly what blocks the dependent figure. A placeholder showing
 *    "2080" would be read as a default even with a caveat beside it, which is the whole reason
 *    DEC-117 refused to default it.
 * 2. **A derived parameter is shown with its value and marked unsettable**, so a reader can
 *    see where the annual cost came from rather than wondering why there is no field for it.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Calculator, Info } from 'lucide-react'
import { getImpactInputs, putImpactInputs } from '../../api/trends'
import { Button } from '../shared/Button'
import { Spinner } from '../shared/Spinner'
import type { ImpactParameter } from '../../types/api'

/** Fraction-valued parameters are entered as percentages, which is how they are discussed. */
const PERCENT_KEYS = new Set(['ktlo_share_actual', 'features_vs_ktlo_split'])

function toDisplay(param: ImpactParameter): string {
  if (param.value == null) return ''
  return PERCENT_KEYS.has(param.key)
    ? String(Math.round(param.value * 1000) / 10)
    : String(param.value)
}

function toStored(key: string, raw: string): number {
  const n = Number(raw)
  return PERCENT_KEYS.has(key) ? n / 100 : n
}

export function ImpactInputsCard() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['impact-inputs'],
    queryFn: getImpactInputs,
  })

  // Seed the draft from what is stored, once. Not a controlled mirror: a re-fetch mid-edit
  // must not overwrite what the user is typing.
  useEffect(() => {
    if (!data) return
    setDraft((prev) =>
      Object.keys(prev).length
        ? prev
        : Object.fromEntries(
            data.parameters.filter((p) => p.settable).map((p) => [p.key, toDisplay(p)]),
          ),
    )
  }, [data])

  const save = useMutation({
    mutationFn: async () => {
      const values: Record<string, number> = {}
      const clear: string[] = []
      for (const [key, raw] of Object.entries(draft)) {
        if (raw.trim() === '') clear.push(key)
        else values[key] = toStored(key, raw.trim())
      }
      return putImpactInputs({ values, clear })
    },
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['impact-inputs'] })
      // The trend page reads these, so its cards must not keep showing a stale blocked state.
      queryClient.invalidateQueries({ queryKey: ['trends'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  if (isLoading || !data) {
    return (
      <div className="bg-panel border border-hairline rounded-md mb-4 py-10 flex justify-center">
        <Spinner />
      </div>
    )
  }

  const settable = data.parameters.filter((p) => p.settable)
  const derived = data.parameters.filter((p) => p.derived_from)

  return (
    <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
        Business impact inputs
      </div>

      <div className="px-4 py-[14px]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-ground border border-hairline flex items-center justify-center flex-none">
            <Calculator className="w-4 h-4 text-accent-text" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] leading-tight">
              {data.missing_customer_inputs.length === 0
                ? 'All inputs supplied'
                : `${settable.length - data.missing_customer_inputs.length} of ${settable.length} supplied`}
            </div>
            <div className="text-[11px] leading-tight text-muted mt-[2px]">
              Values TDIA cannot measure. Leave one blank and the figures that depend on it
              stay hidden rather than being estimated.
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {settable.map((param) => (
            <div key={param.key}>
              <label
                htmlFor={`impact-${param.key}`}
                className="block font-medium text-[12px] mb-1"
              >
                {param.label}
                {PERCENT_KEYS.has(param.key) && (
                  <span className="text-muted font-normal"> (percent)</span>
                )}
              </label>
              <input
                id={`impact-${param.key}`}
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                value={draft[param.key] ?? ''}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, [param.key]: e.target.value }))
                }
                className="w-full border border-control rounded-md px-3 py-2 text-[13px] tabular-nums focus:outline-none focus-visible:shadow-focus"
              />
              <p className="text-[10px] leading-[1.55] text-muted mt-[5px]">{param.caveat}</p>
            </div>
          ))}
        </div>

        {derived.map((param) => (
          <div
            key={param.key}
            className="mt-4 pt-4 border-t border-fill-subtle flex items-start gap-2"
          >
            <Info className="w-[13px] h-[13px] flex-none mt-[3px] text-faint" />
            <div className="min-w-0">
              <div className="font-medium text-[12px]">{param.label}</div>
              <div className="tabular-nums text-[12px] text-body mt-[2px]">
                {param.value == null
                  ? 'Not available yet'
                  : param.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <p className="text-[10px] leading-[1.55] text-muted mt-[4px]">{param.caveat}</p>
            </div>
          </div>
        ))}

        {data.blocked_outputs.length > 0 && (
          <div className="mt-4 pt-4 border-t border-fill-subtle">
            <div className="font-medium text-[12px] mb-2">Currently not shown</div>
            <ul className="space-y-2">
              {data.blocked_outputs.map((block) => (
                <li key={block.metric} className="text-[11px] leading-[1.55] text-muted">
                  <span className="font-medium text-body">{block.metric}</span>, waiting on{' '}
                  {block.missing_parameters.join(' and ')}.
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="text-[12px] text-critical mt-3">{error}</p>}

        <div className="mt-4 flex items-center gap-2">
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
            Save inputs
          </Button>
          {save.isSuccess && !error && (
            <span className="text-[11px] text-muted">Saved.</span>
          )}
        </div>
      </div>
    </div>
  )
}
